import { CommonModule, DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TableModule } from 'primeng/table';
import { Subscription } from 'rxjs';
import { ReportService } from '../../../../services/report.service';
import { ReportsService } from '../../../../services/reports.service';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { saveAs } from 'file-saver';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CommonService } from '../../../../services/common.service';

@Component({
    selector: 'app-engineered-installed-report',
    imports: [TableModule, ChartModule, CommonModule, DatePipe, TableModule, ButtonModule, RouterModule],
    templateUrl: './engineered-installed-report.component.html',
    styleUrl: './engineered-installed-report.component.scss'
})
export class EngineeredInstalledReportComponent {

  isLoading: boolean = false;
  chartOptions: any;
  inspectedInstallReportData: any[] = [];
  chartData: any;
  tabledataShow: boolean = false;
  currentDate: Date = new Date();
  plugins = [ChartDataLabels];
  userCai: string = '';
  sectionTitle: string = '';
  sectionType: string = '';
  isDetailsBlock: boolean = false;
  paramSub: Subscription | undefined;
  selectedTab: string = '';


  constructor(private report: ReportsService, private route: ActivatedRoute, private reportService: ReportService, private router: Router, 
    private commonService: CommonService
  ) { 
    this.userCai = this.commonService.getUserCai();
  }

  ngOnInit() {
    this.paramSub = this.route.queryParams.subscribe(params => {

      if (params['isDetailsBlock'] && params['sectionName'] && params['graphFlag']) {
        this.isDetailsBlock = params['isDetailsBlock'] === 'true';
        this.sectionTitle = params['sectionName'];
        this.sectionType = params['graphFlag'];
        this.getDetailsData(this.sectionTitle, this.sectionType);
        this.setupChartOptions();
      }
      else {
        this.isDetailsBlock =false;
        this.getReportData();
        this.setupChartOptions();
      }
    });
  }

  getReportData() {
    this.isLoading = true;
    let payload = {
      graphFlag: 'G',
      userCai: this.commonService.getUserCai(),
      facility: this.reportService.getCurrentFacilityId(),
      sectionName: 'no',
    };
    this.report.getEngineeredInstallReport(
      payload.graphFlag,
      payload.userCai,
      payload.facility,
      payload.sectionName
    ).subscribe({
      next: (data: any) => {
        this.inspectedInstallReportData = data;
        this.isLoading = false;
        this.chartData = {
          labels: this.inspectedInstallReportData.map((d: any) => d.Section_Abbr),
          datasets: [
            {
              label: 'Installed',
              data: this.inspectedInstallReportData.map((d: any) => d.Installed),
              backgroundColor: '#22c55e',
              borderColor: '#22c55e',
              borderwidth: 1
            },
            {
              label: 'Engineered',
              data: this.inspectedInstallReportData.map((d: any) => d.Engineered),
              backgroundColor: '#f59e0b',
              borderColor: '#f59e0b',
              borderwidth: 1
            },
            {
              label: 'Beyond Due Date',
              data: this.inspectedInstallReportData.map((d: any) => d.Beyond_Due_Date),
              backgroundColor: '#ef4444',
              borderColor: '#ef4444',
              borderwidth: 1
            }
          ]
        };
      },
      error: (error: any) => {
        console.error('Error fetching data:', error);
        this.isLoading = false;
      }
    });

  }

  getDetailsData(title: string, type: string) {
    this.isLoading = true;
    const graphFlag = this.selectedTabFlag();
    const userCAI = this.commonService.getUserCai();
    const facilityId = this.reportService.getCurrentFacilityId();
    const sectionName = title;

    this.report.getEngineeredInstallReport(graphFlag, userCAI, facilityId, sectionName).subscribe({
      next: (data: any) => {
        this.inspectedInstallReportData = data;
        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error fetching data:', error);
        this.isLoading = false;
      }
    });
  }

  setupChartOptions() {
    this.chartOptions = {
      indexAxis: 'x',
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        tooltip: {
          mode: 'index',
          intersect: false
        },
        legend: {
          labels: {
            color: '#495057'
          },
          position: 'bottom'
        },
        datalabels: {
          display: false // Disable the data labels that appear on top of bars
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#495057',
            font: {
              size: 10
            }
          },
          grid: {
            color: '#ebedef'
          }
        },
        y: {
          ticks: {
            color: '#495057'
          },
          grid: {
            color: '#ebedef'
          },
          beginAtZero: true
        }
      }
    };
  }

  prepareChartData() {
    const filteredData = this.inspectedInstallReportData.filter(item =>
      item.installed > 0 || item.engineered > 0 || item.beyondDueDate > 0 || item.otr > 0 || item.otrOverdue > 0
    );
    const labels = filteredData.map(item => {
      // Abbreviate section names for chart
      const words = item.section.split(' ');
      if (words.length > 1) {
        return words.map((word: string) => word.substring(0, 4)).join(' ');
      }
      return item.section.substring(0, 5);
    });

    // Create datasets for the chart
    this.chartData = {
      labels: labels,
      datasets: [
        {
          label: 'Installed',
          backgroundColor: '#22c55e', // Green
          data: filteredData.map(item => item.installed)
        },
        {
          label: 'Inspected',
          backgroundColor: '#f59e0b', // Amber
          data: filteredData.map(item => item.engineered)
        },
        {
          label: 'Beyond Due Date',
          backgroundColor: '#ef4444', // Red
          data: filteredData.map(item => item.beyondDueDate)
        }
      ]
    };
  }
  selectData(event: any) {
    const label = this.chartData.labels[event.element.index];
    const datasetIndex = event.element.datasetIndex;
    const datasetLabel = this.chartData.datasets[datasetIndex].label;
    const sectionData = this.inspectedInstallReportData[event.element.index];

    // Determine which type of data to show based on datasetIndex
    let type = '';
    switch (datasetIndex) {
      case 0:
        type = 'Installed';  // Installed
        break;
      case 1:
        type = 'Engineered';  // Engineered
        break;
      case 2:
        type = 'Beyond Due Date';  // Beyond Due Date
        break;
    }

    if (sectionData) {
      this.getDetails(sectionData, type, datasetLabel);
    }
  }
  getDetails(item: any, type: string, sectionType: string) {
    this.sectionTitle = item.Section_Name;
    this.sectionType = sectionType;
    this.isDetailsBlock = true;
    const queryParams = {
      isDetailsBlock: true,
      graphFlag: this.sectionType,
      sectionName: this.sectionTitle
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge', // This will merge the new query params with existing ones
      skipLocationChange: false // Set to true if you don't want to update the browser URL
    });

  }

  selectedTabFlag(): string {
    switch (this.selectedTab) {
      case 'Installed': return 'i';
      case 'Engineered': return 'n';
      case 'Beyond Due Date': return 'b';
      default: return 'i';
    }
  }

  exportToExcel(): void {
    const facilityId = localStorage.getItem('current_facility_id');

    if (!facilityId) {
      console.error('Facility ID is missing.');
      return;
    }

    const params = {
      graphFlag: this.selectedTabFlag(),
      facilityId: facilityId,
      userCAI: this.userCai,
      sectionName: this.sectionTitle
    };

    this.report.getInspectedInstallReportExcel(params)
      .subscribe({
        next: (fileData: any) => {
          if (fileData) {
            saveAs(fileData, 'Engineered installed report.xlsx');
          }
        },
        error: (err: any) => {
          console.error('Error fetching report details:', err);
        }
      });
  }
  getDateClass(date: Date): string {
    date = new Date(date); // Convert to Date object if it's a string
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for comparison

    if (date < today) {
      return 'past-date';
    } else  {
        return 'future-date';
      }
  }
}
